import React, { useEffect, useState } from "react";
import DashboardHeader from "./dashboard_header";
import SideMenu from "./dashboard_menu";
import Canvas from "./dahboard_canvas";
import DashboardFooter from "./dashboard_footer";
import EHRNav from "./components/EHRNav";
import axios from "axios";
import Swal from "sweetalert2";
import localforage from "localforage";
import { useBaseUrl } from "../../utils/utils";
import { Link } from "react-router-dom";

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface MedicalRecord {
    id: string;
    name: string;
    record_identifier: string;
    chief_complaint: string;
    date_of_service: string;
    privacy_level: string;
    status: string;
    patient: {
        patient_id: string;
        first_name: string;
        last_name: string;
        email: string;
    };
    clinic: { id: number; name: string };
}

interface Clinic { id: number; name: string; email: string; phone: string; }
interface RecordSegment { id: string; name: string; description: string; included: boolean; }
interface ShareRequest {
    id: string;
    patient: any;
    medical_record: any;
    consented_to: any;
    status: string;
    purpose: string;
    scope: string;
    created_at: string;
    start_date: string;
    end_date?: string;
}

const ShareRecords: React.FC = () => {
    // State management
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [isLoading, setIsLoading] = useState(false);

    // Data states
    const [records, setRecords] = useState<MedicalRecord[]>([]);
    const [clinics, setClinics] = useState<Clinic[]>([]);
    const [shareHistory, setShareHistory] = useState<ShareRequest[]>([]);
    
    // Selection states
    const [selectedRecord, setSelectedRecord] = useState<MedicalRecord | null>(null);
    const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
    const [selectedSegments, setSelectedSegments] = useState<RecordSegment[]>([]);
    
    // Form states
    const [purpose, setPurpose] = useState<string>("");
    const [customScope, setCustomScope] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [recordSearchTerm, setRecordSearchTerm] = useState<string>("");
    
    // Filter states for history
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [dateFilter, setDateFilter] = useState<string>("all");
    const [expiryDuration, setExpiryDuration] = useState<string>("7d");

    const base_url = useBaseUrl();

    useEffect(() => {
        document.title = "Share Medical Records - Uzima EHR";
        validateUserAndLoadData();
    }, []);

    const validateUserAndLoadData = async () => {
        try {
            // Validate user authentication and clinic access
            const clinic_id = await localforage.getItem("clinic_id");
            const staff_id = await localforage.getItem("staff_id");
            
            if (!clinic_id || !staff_id) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Required",
                    text: "Please log in to access medical record sharing",
                    confirmButtonText: "Go to Login"
                }).then(() => {
                    window.location.href = "/institution/newlog";
                });
                return;
            }
            
            await loadInitialData();
        } catch (error) {
            console.error("Authentication validation failed:", error);
            Swal.fire({
                icon: "error",
                title: "Authentication Error",
                text: "Unable to validate your access. Please log in again."
            });
        }
    };

    const loadInitialData = async () => {
        await Promise.all([
            loadMedicalRecords(),
            loadClinics(),
            loadShareHistory()
        ]);
    };

    // Load medical records for the current clinic
    const loadMedicalRecords = async () => {
        try {
            const clinic_id = await localforage.getItem("clinic_id");
            const staff_id = await localforage.getItem("staff_id");
            
            if (!clinic_id || !staff_id) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Required",
                    text: "Please log in to access medical records",
                    confirmButtonText: "Go to Login"
                }).then(() => {
                    window.location.href = "/institution/newlog";
                });
                return;
            }

            const authHeaders = {
                'Content-Type': 'application/json',
                'X-Clinic-ID': clinic_id?.toString() || '',
                'X-Staff-ID': staff_id?.toString() || ''
            };

            const response = await axios.get(
                `${base_url}ehr/api/v1/medical-records/?clinic_id=${clinic_id}`,
                { headers: authHeaders, timeout: 15000 }
            );
            
            if (response.data.status === "success") {
                setRecords(response.data.data);
            }
        } catch (error: any) {
            console.error("Error loading medical records:", error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                Swal.fire({
                    icon: "error",
                    title: "Authentication Error",
                    text: "Your session has expired. Please log in again.",
                    confirmButtonText: "Go to Login"
                }).then(() => {
                    window.location.href = "/institution/newlog";
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to load medical records. Please try again."
                });
            }
        }
    };

    // Load available clinics for sharing
    const loadClinics = async () => {
        try {
            const clinic_id = await localforage.getItem("clinic_id");
            const staff_id = await localforage.getItem("staff_id");
            
            if (!clinic_id || !staff_id) {
                return;
            }

            const authHeaders = {
                'Content-Type': 'application/json',
                'X-Clinic-ID': clinic_id?.toString() || '',
                'X-Staff-ID': staff_id?.toString() || ''
            };

            const response = await axios.get(
                `${base_url}facilities/clinics/all`,
                { headers: authHeaders, timeout: 15000 }
            );
            
            if (response.data.status === "success") {
                // Filter out the current clinic
                const filteredClinics = response.data.data.filter((clinic: any) => clinic.id !== clinic_id);
                setClinics(filteredClinics);
            }
        } catch (error: any) {
            console.error("Error loading clinics:", error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                // Silent fail for clinics loading - main auth check is in medical records
                console.warn("Authentication required for clinic loading");
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to load clinics"
                });
            }
        }
    };

    // Load sharing history
    const loadShareHistory = async () => {
        try {
            const clinic_id = await localforage.getItem("clinic_id");
            const staff_id = await localforage.getItem("staff_id");
            
            if (!clinic_id || !staff_id) {
                return;
            }

            const authHeaders = {
                'Content-Type': 'application/json',
                'X-Clinic-ID': clinic_id?.toString() || '',
                'X-Staff-ID': staff_id?.toString() || ''
            };

            const response = await axios.get(
                `${base_url}ehr/api/v1/sharing-audit/sharing_history/?clinic_id=${clinic_id}`,
                { headers: authHeaders, timeout: 15000 }
            );
            
            if (response.data.status === "success") {
                setShareHistory(response.data.data.history || []);
            }
        } catch (error: any) {
            console.error("Error loading share history:", error);
            // Don't show error for history - it's optional
        }
    };

    // Generate record segments for selection
    const generateRecordSegments = (record: MedicalRecord): RecordSegment[] => {
        return [
            {
                id: "patient_info",
                name: "Patient Information",
                description: "Basic patient demographics and contact information",
                included: true
            },
            {
                id: "chief_complaint",
                name: "Chief Complaint",
                description: record.chief_complaint,
                included: true
            },
            {
                id: "medical_history",
                name: "Medical History",
                description: "Past medical history, family history, and social history",
                included: true
            },
            {
                id: "medications",
                name: "Medications",
                description: "Current and past medications",
                included: false
            },
            {
                id: "vital_signs",
                name: "Vital Signs",
                description: "Blood pressure, heart rate, temperature, etc.",
                included: false
            },
            {
                id: "lab_results",
                name: "Laboratory Results",
                description: "Blood tests, urine tests, and other lab work",
                included: false
            },
            {
                id: "imaging_results",
                name: "Imaging Results",
                description: "X-rays, MRIs, CT scans, and other imaging",
                included: false
            },
            {
                id: "provider_notes",
                name: "Provider Notes",
                description: "Clinical observations and treatment notes",
                included: false
            }
        ];
    };

    // Handle record selection
    const handleRecordSelect = (record: MedicalRecord) => {
        setSelectedRecord(record);
        setSelectedSegments(generateRecordSegments(record));
        setCurrentStep(2);
    };

    // Handle clinic selection
    const handleClinicSelect = (clinic: Clinic) => {
        setSelectedClinic(clinic);
        setCurrentStep(3);
    };

    // Toggle segment inclusion
    const toggleSegment = (segmentId: string) => {
        setSelectedSegments(prev => 
            prev.map(segment => 
                segment.id === segmentId 
                    ? { ...segment, included: !segment.included }
                    : segment
            )
        );
    };

    // Create share request
    const createShareRequest = async () => {
        // Enhanced validation
        if (!selectedRecord || !selectedClinic || !purpose.trim()) {
            Swal.fire({
                icon: "warning",
                title: "Incomplete Information",
                text: "Please ensure all required fields are filled"
            });
            return;
        }

        // Validate purpose length and content
        if (purpose.trim().length < 10) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Purpose",
                text: "Purpose must be at least 10 characters long"
            });
            return;
        }

        if (purpose.trim().length > 500) {
            Swal.fire({
                icon: "warning",
                title: "Purpose Too Long",
                text: "Purpose cannot exceed 500 characters"
            });
            return;
        }

        // Validate that at least one segment is selected
        const includedSegments = selectedSegments.filter(s => s.included);
        if (includedSegments.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "No Data Selected",
                text: "Please select at least one data segment to share"
            });
            return;
        }

        setIsLoading(true);

        try {
            // Validate user authentication before making request
            const clinic_id = await localforage.getItem("clinic_id");
            const staff_id = await localforage.getItem("staff_id");
            
            if (!clinic_id || !staff_id) {
                throw new Error("Authentication session expired. Please log in again.");
            }

            const scopeDescription = customScope || `Selected segments: ${includedSegments.map(s => s.name).join(', ')}`;

            // Calculate expiry date from duration
            const now = new Date();
            let expiryDate: string | null = null;
            const durationMap: Record<string, number> = { '1h': 1/24, '6h': 6/24, '1d': 1, '3d': 3, '7d': 7, '14d': 14, '30d': 30, '90d': 90 };
            const days = durationMap[expiryDuration];
            if (days) {
                const expiry = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
                expiryDate = expiry.toISOString();
            }

            const shareData = {
                medical_record_id: selectedRecord.id,
                clinic_id: selectedClinic.id,
                purpose: purpose.trim(),
                scope: scopeDescription.trim(),
                selected_segments: includedSegments,
                end_date: expiryDate
            };

            // Add authentication headers
            const authHeaders = {
                'Content-Type': 'application/json',
                'X-Clinic-ID': clinic_id?.toString() || '',
                'X-Staff-ID': staff_id?.toString() || ''
            };

            const response = await axios.post(
                `${base_url}ehr/api/v1/share-requests/create_share_request/`,
                shareData,
                { headers: authHeaders, timeout: 15000 }
            );

            if (response.data.status === "success") {
                await Swal.fire({
                    icon: "success",
                    title: "Share Request Created",
                    text: `A consent request has been sent to ${selectedRecord.patient.first_name} ${selectedRecord.patient.last_name}. They will need to approve this request before the record can be shared.`,
                    confirmButtonText: "Continue"
                });

                // Reset form and reload data
                resetForm();
                await loadShareHistory();
            } else {
                throw new Error(response.data.message || "Failed to create share request");
            }
        } catch (error: any) {
            console.error("Error creating share request:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Failed to create share request"
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setCurrentStep(1);
        setSelectedRecord(null);
        setSelectedClinic(null);
        setSelectedSegments([]);
        setPurpose("");
        setCustomScope("");
        setExpiryDuration("7d");
        setSearchTerm("");
        setRecordSearchTerm("");
    };

    // Filter functions
    const filteredRecords = records.filter(record => 
        !recordSearchTerm || 
        record.name.toLowerCase().includes(recordSearchTerm.toLowerCase()) ||
        record.patient.first_name.toLowerCase().includes(recordSearchTerm.toLowerCase()) ||
        record.patient.last_name.toLowerCase().includes(recordSearchTerm.toLowerCase()) ||
        record.patient.patient_id.toLowerCase().includes(recordSearchTerm.toLowerCase()) ||
        record.chief_complaint.toLowerCase().includes(recordSearchTerm.toLowerCase())
    );

    const filteredClinics = clinics.filter(clinic => 
        !searchTerm || 
        clinic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        clinic.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredHistory = shareHistory.filter(request => {
        if (statusFilter !== "all" && request.status !== statusFilter) return false;
        if (dateFilter !== "all") {
            const requestDate = new Date(request.created_at);
            const now = new Date();
            switch (dateFilter) {
                case "today":
                    return requestDate.toDateString() === now.toDateString();
                case "week":
                    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    return requestDate >= weekAgo;
                case "month":
                    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    return requestDate >= monthAgo;
                default:
                    return true;
            }
        }
        return true;
    });

    // Format date function
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge
    const getStatusBadge = (status: string) => {
        const statusConfig = {
            pending: { class: "bg-warning", text: "Pending Patient Approval" },
            active: { class: "bg-success", text: "Active - Shared" },
            rejected: { class: "bg-danger", text: "Rejected by Patient" },
            expired: { class: "bg-secondary", text: "Expired" },
            inactive: { class: "bg-dark", text: "Inactive" }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        return <span className={`badge ${config.class}`}>{config.text}</span>;
    };

    const stepLabels = [
        { icon: 'bx bx-file', label: 'Select Record' },
        { icon: 'bx bx-building-house', label: 'Choose Facility' },
        { icon: 'bx bx-check-square', label: 'Select Data' },
        { icon: 'bx bx-send', label: 'Send Request' },
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-export me-2" style={{ color: ACCENT }}></i>Share Records</h1>
                                <p>Share medical records with other facilities securely</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={() => setCurrentStep(5)}><i className="bx bx-history"></i> History</button>
                                <button className="sc-btn sc-btn-primary" onClick={resetForm}><i className="bx bx-plus"></i> New Share</button>
                            </div>
                        </div>

                        <EHRNav />

                        {/* Step Indicator */}
                        {currentStep <= 4 && (
                            <div className="sc-card" style={{ marginBottom: 24 }}>
                                <div className="sc-card-body" style={{ padding: '16px 24px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                        {stepLabels.map((s, i) => (
                                            <React.Fragment key={i}>
                                                {i > 0 && <div style={{ flex: 1, height: 2, background: currentStep > i ? ACCENT : '#e2e8f0', margin: '0 8px', transition: 'background 0.3s' }}></div>}
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, opacity: currentStep >= i + 1 ? 1 : 0.4, transition: 'all 0.3s' }}>
                                                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: currentStep >= i + 1 ? ACCENT : '#e2e8f0', color: currentStep >= i + 1 ? '#fff' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', transition: 'all 0.3s' }}>
                                                        <i className={s.icon}></i>
                                                    </div>
                                                    <span style={{ fontSize: '0.68rem', fontWeight: 600, color: currentStep >= i + 1 ? ACCENT : '#94a3b8', whiteSpace: 'nowrap' }}>{s.label}</span>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 1: Select Record */}
                        {currentStep === 1 && (
                            <div className="sc-card">
                                <div className="sc-card-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--uzima-shell-text)' }}><i className="bx bx-file me-2" style={{ color: ACCENT }}></i>Select Medical Record</div>
                                        <div className="sc-search" style={{ maxWidth: 300, marginBottom: 0 }}>
                                            <i className="bx bx-search"></i>
                                            <input type="text" placeholder="Search records..." value={recordSearchTerm} onChange={e => setRecordSearchTerm(e.target.value)} />
                                        </div>
                                    </div>
                                    {filteredRecords.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <div style={{ width: 56, height: 56, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><i className="bx bx-file" style={{ fontSize: '1.4rem', color: ACCENT }}></i></div>
                                            <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)' }}>No Records Found</p>
                                            <p style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)' }}>{recordSearchTerm ? "No records match your search" : "No medical records available"}</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                                            {filteredRecords.map(r => (
                                                <div key={r.id} className="sc-card" style={{ cursor: 'pointer', transition: 'all 0.15s', margin: 0 }}
                                                    onClick={() => handleRecordSelect(r)}
                                                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = ACCENT; }}
                                                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = ''; }}
                                                >
                                                    <div className="sc-card-body" style={{ padding: '14px 16px' }}>
                                                        <div style={{ fontWeight: 700, fontSize: '0.84rem', marginBottom: 4 }}>{r.name}</div>
                                                        <div style={{ fontSize: '0.76rem', color: ACCENT, fontWeight: 600, marginBottom: 4 }}>{r.patient.first_name} {r.patient.last_name}</div>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 8 }}>{r.chief_complaint}</div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span style={{ padding: '2px 8px', borderRadius: 20, background: r.privacy_level === 'confidential' ? 'rgba(244,63,94,0.1)' : ACCENT_SOFT, color: r.privacy_level === 'confidential' ? '#e11d48' : ACCENT, fontSize: '0.65rem', fontWeight: 700 }}>{r.privacy_level}</span>
                                                            <span style={{ fontSize: '0.7rem', color: 'var(--uzima-shell-text-muted)' }}>{formatDate(r.date_of_service)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 2: Choose Facility */}
                        {currentStep === 2 && (
                            <div className="sc-card">
                                <div className="sc-card-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--uzima-shell-text)' }}><i className="bx bx-building-house me-2" style={{ color: ACCENT }}></i>Choose Recipient Facility</div>
                                            {selectedRecord && <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)', marginTop: 4 }}>Sharing: <b>{selectedRecord.name}</b> for {selectedRecord.patient.first_name} {selectedRecord.patient.last_name}</div>}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setCurrentStep(1)}><i className="bx bx-arrow-back"></i> Back</button>
                                            <div className="sc-search" style={{ maxWidth: 250, marginBottom: 0 }}>
                                                <i className="bx bx-search"></i>
                                                <input type="text" placeholder="Search clinics..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                    {filteredClinics.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <p style={{ fontWeight: 600, color: 'var(--uzima-shell-text)' }}>No Clinics Found</p>
                                            <p style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)' }}>No facilities available for sharing</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                                            {filteredClinics.map(c => (
                                                <div key={c.id} className="sc-card" style={{ cursor: 'pointer', transition: 'all 0.15s', margin: 0 }}
                                                    onClick={() => handleClinicSelect(c)}
                                                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = ACCENT; }}
                                                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = ''; }}
                                                >
                                                    <div className="sc-card-body" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <div style={{ width: 40, height: 40, borderRadius: 12, background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <i className="bx bx-building-house" style={{ fontSize: '1.1rem', color: ACCENT }}></i>
                                                        </div>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}>{c.name}</div>
                                                            <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{c.email}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Step 3: Select Data Segments + Purpose */}
                        {currentStep === 3 && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div className="sc-card" style={{ margin: 0 }}>
                                    <div className="sc-card-body">
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--uzima-shell-text)', marginBottom: 14 }}><i className="bx bx-check-square me-2" style={{ color: ACCENT }}></i>Select Data Segments</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {selectedSegments.map(seg => (
                                                <label key={seg.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 10, background: seg.included ? ACCENT_SOFT : 'rgba(15,23,42,0.02)', border: `1px solid ${seg.included ? ACCENT : 'var(--uzima-shell-border)'}`, cursor: 'pointer', transition: 'all 0.15s' }}>
                                                    <input type="checkbox" checked={seg.included} onChange={() => toggleSegment(seg.id)} style={{ marginTop: 2, accentColor: ACCENT }} />
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{seg.name}</div>
                                                        <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{seg.description}</div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="sc-card" style={{ margin: 0 }}>
                                    <div className="sc-card-body">
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--uzima-shell-text)', marginBottom: 14 }}><i className="bx bx-notepad me-2" style={{ color: ACCENT }}></i>Sharing Details</div>
                                        <div style={{ marginBottom: 14 }}>
                                            <label style={{ fontSize: '0.76rem', fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--uzima-shell-text)' }}>Purpose of Sharing *</label>
                                            <textarea value={purpose} onChange={e => setPurpose(e.target.value)} rows={4} placeholder="Describe the medical purpose for sharing this record (min 10 chars)..."
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', resize: 'vertical', fontFamily: 'inherit' }}
                                            />
                                            <div style={{ fontSize: '0.68rem', color: 'var(--uzima-shell-text-muted)', marginTop: 4 }}>{purpose.length}/500 characters</div>
                                        </div>
                                        <div style={{ marginBottom: 14 }}>
                                            <label style={{ fontSize: '0.76rem', fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--uzima-shell-text)' }}>Auto-Expiry Duration *</label>
                                            <select value={expiryDuration} onChange={e => setExpiryDuration(e.target.value)}
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem', background: '#fff' }}>
                                                <option value="1h">1 Hour</option>
                                                <option value="6h">6 Hours</option>
                                                <option value="1d">1 Day</option>
                                                <option value="3d">3 Days</option>
                                                <option value="7d">7 Days (Default)</option>
                                                <option value="14d">14 Days</option>
                                                <option value="30d">30 Days</option>
                                                <option value="90d">90 Days</option>
                                            </select>
                                            <div style={{ fontSize: '0.68rem', color: '#d97706', marginTop: 4, fontWeight: 600 }}><i className="bx bx-time-five me-1"></i>Access will automatically expire after this duration</div>
                                        </div>
                                        <div style={{ marginBottom: 14 }}>
                                            <label style={{ fontSize: '0.76rem', fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--uzima-shell-text)' }}>Custom Scope (optional)</label>
                                            <input type="text" value={customScope} onChange={e => setCustomScope(e.target.value)} placeholder="e.g. Treatment continuity, second opinion..."
                                                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--uzima-shell-border)', borderRadius: 10, fontSize: '0.82rem' }}
                                            />
                                        </div>

                                        {/* Summary */}
                                        <div style={{ padding: 14, borderRadius: 10, background: 'rgba(15,23,42,0.025)', marginTop: 16 }}>
                                            <div style={{ fontSize: '0.76rem', fontWeight: 700, marginBottom: 8, color: 'var(--uzima-shell-text)' }}>Summary</div>
                                            <div style={{ fontSize: '0.74rem', color: 'var(--uzima-shell-text-muted)', lineHeight: 1.7 }}>
                                                <div><b>Record:</b> {selectedRecord?.name}</div>
                                                <div><b>Patient:</b> {selectedRecord?.patient.first_name} {selectedRecord?.patient.last_name}</div>
                                                <div><b>Facility:</b> {selectedClinic?.name}</div>
                                                <div><b>Segments:</b> {selectedSegments.filter(s => s.included).length} of {selectedSegments.length}</div>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                            <button className="sc-btn sc-btn-ghost" onClick={() => setCurrentStep(2)}><i className="bx bx-arrow-back"></i> Back</button>
                                            <button className="sc-btn sc-btn-primary" style={{ flex: 1 }} onClick={() => {
                                                if (!purpose.trim() || purpose.trim().length < 10) {
                                                    Swal.fire({ icon: 'warning', title: 'Purpose Required', text: 'Please provide a purpose (min 10 characters).' });
                                                    return;
                                                }
                                                if (selectedSegments.filter(s => s.included).length === 0) {
                                                    Swal.fire({ icon: 'warning', title: 'No Data Selected', text: 'Select at least one data segment to share.' });
                                                    return;
                                                }
                                                setCurrentStep(4);
                                            }}><i className="bx bx-right-arrow-alt"></i> Review & Send</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 4: Review & Confirm */}
                        {currentStep === 4 && (
                            <div className="sc-card">
                                <div className="sc-card-body">
                                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--uzima-shell-text)', marginBottom: 16 }}><i className="bx bx-send me-2" style={{ color: ACCENT }}></i>Review & Send Share Request</div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                                        <div style={{ padding: 16, borderRadius: 12, background: ACCENT_SOFT }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: ACCENT, marginBottom: 8 }}>Record</div>
                                            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{selectedRecord?.name}</div>
                                            <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>Patient: {selectedRecord?.patient.first_name} {selectedRecord?.patient.last_name}</div>
                                        </div>
                                        <div style={{ padding: 16, borderRadius: 12, background: 'rgba(59,130,246,0.06)' }}>
                                            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#2563eb', marginBottom: 8 }}>Recipient</div>
                                            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{selectedClinic?.name}</div>
                                            <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}>{selectedClinic?.email}</div>
                                        </div>
                                    </div>

                                    <div style={{ padding: 16, borderRadius: 12, background: 'rgba(15,23,42,0.025)', marginBottom: 16 }}>
                                        <div style={{ fontSize: '0.76rem', fontWeight: 700, marginBottom: 8 }}>Purpose</div>
                                        <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text)', margin: 0 }}>{purpose}</p>
                                    </div>

                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ fontSize: '0.76rem', fontWeight: 700, marginBottom: 8 }}>Data Segments Included</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {selectedSegments.filter(s => s.included).map(s => (
                                                <span key={s.id} style={{ padding: '4px 12px', borderRadius: 20, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.72rem', fontWeight: 600 }}>{s.name}</span>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ padding: 16, borderRadius: 12, background: 'rgba(139,92,246,0.06)', marginBottom: 16 }}>
                                        <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#8b5cf6', marginBottom: 8 }}>Auto-Expiry</div>
                                        <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{{'1h':'1 Hour','6h':'6 Hours','1d':'1 Day','3d':'3 Days','7d':'7 Days','14d':'14 Days','30d':'30 Days','90d':'90 Days'}[expiryDuration] || expiryDuration}</div>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>Access will be automatically revoked after this period</div>
                                    </div>

                                    <div style={{ padding: 14, borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 20 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', fontWeight: 600, color: '#d97706' }}>
                                            <i className="bx bx-info-circle"></i>
                                            The patient will receive a consent request and must approve before the record is shared.
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button className="sc-btn sc-btn-ghost" onClick={() => setCurrentStep(3)}><i className="bx bx-arrow-back"></i> Back</button>
                                        <button className="sc-btn sc-btn-primary" style={{ flex: 1 }} onClick={createShareRequest} disabled={isLoading}>
                                            {isLoading ? <><i className="bx bx-loader-alt bx-spin"></i> Sending...</> : <><i className="bx bx-send"></i> Send Share Request</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 5: Sharing History */}
                        {currentStep === 5 && (
                            <div className="sc-card">
                                <div className="sc-card-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--uzima-shell-text)' }}><i className="bx bx-history me-2" style={{ color: ACCENT }}></i>Sharing History & Audit Trail</div>
                                        <button className="sc-btn sc-btn-primary sc-btn-sm" onClick={resetForm}><i className="bx bx-plus"></i> New Share</button>
                                    </div>
                                    {filteredHistory.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <div style={{ width: 56, height: 56, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><i className="bx bx-history" style={{ fontSize: '1.4rem', color: ACCENT }}></i></div>
                                            <p style={{ fontWeight: 600 }}>No Sharing History</p>
                                            <p style={{ fontSize: '0.8rem', color: 'var(--uzima-shell-text-muted)' }}>No sharing requests yet</p>
                                        </div>
                                    ) : (
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                                <thead>
                                                    <tr style={{ background: 'rgba(15,23,42,0.025)' }}>
                                                        {['Patient', 'Record', 'Shared With', 'Purpose', 'Status', 'Date'].map((h, i) => (
                                                            <th key={i} style={{ padding: '10px 14px', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', borderBottom: '1px solid var(--uzima-shell-border)' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredHistory.map(req => (
                                                        <tr key={req.id} style={{ borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                                            <td style={{ padding: '10px 14px' }}><b>{req.patient?.first_name} {req.patient?.last_name}</b></td>
                                                            <td style={{ padding: '10px 14px' }}>{req.medical_record?.name}</td>
                                                            <td style={{ padding: '10px 14px' }}>{req.consented_to?.name}</td>
                                                            <td style={{ padding: '10px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={req.purpose}>{req.purpose}</td>
                                                            <td style={{ padding: '10px 14px' }}>{getStatusBadge(req.status)}</td>
                                                            <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>{formatDate(req.created_at)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default ShareRecords;
